import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Coins, LogOut, KeyRound, ShieldOff, ShieldCheck, Trash2, RefreshCw, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const TABS = ["overview", "credits", "sessions", "activity", "generations", "projects", "files", "renders", "errors", "audit"];
const PLANS = ["free", "trial", "starter", "professional", "enterprise"];
const ROLES = ["user", "admin", "super_admin"];

const StatusBadge = ({ status }) => {
  const map = { active: "text-[#34D399] border-[#34D399]/40 bg-[#34D399]/10", suspended: "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10", deleted: "text-red-400 border-red-400/40 bg-red-400/10" };
  return <span className={`text-[10px] px-2 py-0.5 border ${map[status] || "text-white/40 border-white/20"} rounded-sm uppercase font-mono`}>{status}</span>;
};

export default function AdminUserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: current } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [tabData, setTabData] = useState({});
  const [dialogs, setDialogs] = useState({});

  const isSuper = current?.role === "super_admin";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setData(data);
    } catch (e) {
      toast.error("Could not load user");
      nav("/admin/users");
    }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const loadTab = useCallback(async (name) => {
    if (name === "overview" || tabData[name]) return;
    try {
      const path = name === "renders" ? `/admin/users/${id}/renders` : `/admin/users/${id}/${name}`;
      const { data } = await api.get(path);
      setTabData((td) => ({ ...td, [name]: data }));
    } catch (e) { /* ignore */ }
  }, [id, tabData]);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  if (!data) return <div className="p-8 text-white/40 text-sm">Loading user…</div>;
  const u = data.user;
  const ov = data.overview;

  const refresh = () => { setTabData({}); load(); loadTab(tab); };

  return (
    <div className="p-8 max-w-[1600px]">
      <button onClick={() => nav("/admin/users")} className="text-xs text-white/50 hover:text-white flex items-center gap-1" data-testid="admin-user-back"><ArrowLeft className="h-3 w-3" /> Back to users</button>

      {/* Header */}
      <div className="mt-3 kr-card p-6 flex flex-wrap items-start gap-6" data-testid="admin-user-header">
        <div className="w-14 h-14 rounded-full bg-[#8A52FF]/20 border border-[#8A52FF]/40 flex items-center justify-center">
          {u.picture ? <img src={u.picture} alt="" className="w-full h-full rounded-full object-cover" /> : <UserIcon className="h-6 w-6 text-[#8A52FF]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-2xl">{u.name || u.email}</h1>
            <StatusBadge status={u.status || "active"} />
            <span className="text-[10px] px-1.5 py-0.5 border border-[#00E5FF]/40 text-[#00E5FF] rounded-sm uppercase font-mono">{u.role}</span>
            <span className="text-[10px] px-1.5 py-0.5 border border-white/20 text-white/70 rounded-sm uppercase font-mono">{u.plan}</span>
          </div>
          <div className="text-white/50 text-sm font-mono mt-1">{u.email}</div>
          <div className="text-white/30 text-[10px] font-mono mt-0.5">ID {u.user_id} · Registered {(u.created_at || "").slice(0, 10)} · Last login {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-right">
            <div className="kr-overline">CREDITS</div>
            <div className="font-display text-2xl">{Number(u.credits || 0).toLocaleString()}</div>
          </div>
          <div className="w-px h-10 bg-white/10 mx-1" />
          {isSuper && (
            <>
              <Button size="sm" variant="outline" onClick={() => setDialogs({ credits: true })} className="border-white/10 h-8" data-testid="admin-user-edit-credits"><Coins className="h-3.5 w-3.5 mr-1.5" /> Credits</Button>
              <Button size="sm" variant="outline" onClick={() => setDialogs({ edit: true })} className="border-white/10 h-8" data-testid="admin-user-edit"><UserIcon className="h-3.5 w-3.5 mr-1.5" /> Edit</Button>
              {u.status === "active" ? (
                <Button size="sm" variant="outline" onClick={() => setDialogs({ suspend: true })} className="border-[#F59E0B]/40 text-[#F59E0B] h-8" data-testid="admin-user-suspend"><ShieldOff className="h-3.5 w-3.5 mr-1.5" /> Suspend</Button>
              ) : u.status === "suspended" ? (
                <Button size="sm" variant="outline" onClick={() => act("/reactivate", "Reactivated")} className="border-[#34D399]/40 text-[#34D399] h-8" data-testid="admin-user-reactivate"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Reactivate</Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => act("/restore", "Restored")} className="border-[#34D399]/40 text-[#34D399] h-8" data-testid="admin-user-restore"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Restore</Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setDialogs({ reset: true })} className="border-white/10 h-8" data-testid="admin-user-reset-password"><KeyRound className="h-3.5 w-3.5 mr-1.5" /> Reset password</Button>
              <Button size="sm" variant="outline" onClick={() => act("/force-logout", "All sessions killed")} className="border-white/10 h-8" data-testid="admin-user-force-logout"><LogOut className="h-3.5 w-3.5 mr-1.5" /> Force logout</Button>
              <Button size="sm" variant="outline" onClick={() => setDialogs({ del: true })} className="border-red-500/40 text-red-300 h-8" data-testid="admin-user-delete"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete</Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-[#050505] border border-white/5 rounded-sm h-auto flex-wrap">
          {TABS.map((t) => (<TabsTrigger key={t} value={t} className="data-[state=active]:bg-white/10 data-[state=active]:text-white capitalize px-4 py-1.5" data-testid={`admin-user-tab-${t}`}>{t}</TabsTrigger>))}
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(ov).map(([k, v]) => (
              <div key={k} className="kr-card p-4">
                <div className="kr-overline">{k.replace(/_/g, " ")}</div>
                <div className="font-display text-2xl mt-2">{Number(v || 0).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sessions" className="mt-4">
          <FeedTable rows={tabData.sessions?.items || []} columns={[
            { key: "login_at", label: "Login", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
            { key: "ip", label: "IP" },
            { key: "geo", label: "Location", fmt: (v) => v ? [v.city, v.region, v.country_code].filter(Boolean).join(", ") : "—" },
            { key: "device", label: "Device" }, { key: "browser", label: "Browser" }, { key: "os", label: "OS" },
            { key: "status", label: "Status" },
            { key: "success", label: "Success", fmt: (v) => v ? "✓" : "✗" },
          ]} />
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <FeedTable rows={tabData.activity?.items || []} columns={[
            { key: "created_at", label: "When", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
            { key: "module", label: "Module" },
            { key: "action", label: "Action" },
            { key: "target_name", label: "Target", fmt: (v) => (v || "").slice(0, 40) },
            { key: "status", label: "Status" },
            { key: "credits_used", label: "Credits", align: "right" },
            { key: "imported", label: "Imported", fmt: (v) => v ? "yes" : "" },
            { key: "ip", label: "IP" },
          ]} />
        </TabsContent>

        <TabsContent value="generations" className="mt-4">
          <FeedTable rows={tabData.generations?.items || []} columns={[
            { key: "created_at", label: "When", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
            { key: "agent", label: "Agent" }, { key: "kind", label: "Kind" },
            { key: "prompt", label: "Prompt", fmt: (v) => (v || "").slice(0, 60) },
            { key: "status", label: "Status" },
          ]} />
        </TabsContent>

        <TabsContent value="projects" className="mt-4">
          <div className="space-y-6">
            <ProjectSection title="Projects" items={tabData.projects?.projects || []} keyField="project_id" />
            <ProjectSection title="Video Editor projects" items={tabData.projects?.video_editor_projects || []} keyField="ve_id" />
            <ProjectSection title="Websites" items={tabData.projects?.websites || []} keyField="website_id" />
          </div>
        </TabsContent>

        <TabsContent value="files" className="mt-4">
          <FeedTable rows={tabData.files?.items || []} columns={[
            { key: "created_at", label: "When", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
            { key: "name", label: "Name", fmt: (v) => (v || "").slice(0, 60) },
            { key: "type", label: "Type" }, { key: "source", label: "Source" },
            { key: "size", label: "Size", align: "right", fmt: (v) => fmtBytes(v || 0) },
          ]} />
        </TabsContent>

        <TabsContent value="renders" className="mt-4">
          <FeedTable rows={tabData.renders?.items || []} columns={[
            { key: "created_at", label: "When", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
            { key: "resolution", label: "Res" }, { key: "fps", label: "FPS", align: "right" },
            { key: "status", label: "Status" }, { key: "progress", label: "%", align: "right" },
            { key: "cost", label: "Credits", align: "right" },
            { key: "error", label: "Error", fmt: (v) => (v || "").slice(0, 40) },
          ]} />
        </TabsContent>

        <TabsContent value="errors" className="mt-4">
          <FeedTable rows={tabData.errors?.items || []} columns={[
            { key: "created_at", label: "When", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
            { key: "module", label: "Module" }, { key: "action", label: "Action" },
            { key: "error_message", label: "Error", fmt: (v) => (v || "").slice(0, 80) },
          ]} />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <FeedTable rows={tabData.audit?.items || []} columns={[
            { key: "created_at", label: "When", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
            { key: "admin_email", label: "Admin" },
            { key: "action", label: "Action" },
            { key: "previous_value", label: "Before", fmt: (v) => JSON.stringify(v || null).slice(0, 60) },
            { key: "new_value", label: "After", fmt: (v) => JSON.stringify(v || null).slice(0, 60) },
            { key: "reason", label: "Reason" },
            { key: "admin_ip", label: "IP" },
          ]} />
        </TabsContent>

        <TabsContent value="credits" className="mt-4">
          <div className="kr-card p-6">
            <div className="kr-overline mb-3">CREDIT LEDGER</div>
            <FeedTable rows={(tabData.activity?.items || []).filter((e) => e.module === "billing")} columns={[
              { key: "created_at", label: "When", fmt: (v) => v ? new Date(v).toLocaleString() : "—" },
              { key: "action", label: "Action" },
              { key: "meta", label: "Amount", fmt: (v) => v?.amount ?? "" },
              { key: "meta", label: "Balance after", fmt: (v) => v?.balance_after ?? "" },
            ]} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {dialogs.credits && <CreditsDialog userId={id} onClose={() => { setDialogs({}); refresh(); }} />}
      {dialogs.edit && <EditDialog user={u} onClose={() => { setDialogs({}); refresh(); }} />}
      {dialogs.suspend && <ReasonDialog title="Suspend user" endpoint={`/admin/users/${id}/suspend`} onClose={() => { setDialogs({}); refresh(); }} />}
      {dialogs.reset && <ResetPasswordDialog userId={id} onClose={() => { setDialogs({}); refresh(); }} />}
      {dialogs.del && <DeleteDialog userId={id} email={u.email} onClose={() => { setDialogs({}); }} onDeleted={() => nav("/admin/users")} />}
    </div>
  );

  async function act(path, msg) {
    try {
      await api.post(`/admin/users/${id}${path}`, {});
      toast.success(msg);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  }
}

const fmtBytes = (n) => n < 1024 ? `${n} B` : n < 1024**2 ? `${(n/1024).toFixed(1)} KB` : n < 1024**3 ? `${(n/1024**2).toFixed(1)} MB` : `${(n/1024**3).toFixed(2)} GB`;

function FeedTable({ rows, columns }) {
  if (!rows || rows.length === 0) return <div className="text-white/40 text-xs py-12 text-center border border-white/5">No records.</div>;
  return (
    <div className="border border-white/5 overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-[#050505] text-white/50">
          <tr>{columns.map((c) => <th key={c.label} className={`px-3 py-2 font-normal ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.event_id || r.session_id || r.gen_id || r.file_id || r.render_id || r.audit_id || i} className="border-t border-white/5">
              {columns.map((c, ci) => (
                <td key={ci} className={`px-3 py-2 ${c.align === "right" ? "text-right font-mono" : ""} text-white/80`}>{c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? "")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectSection({ title, items, keyField }) {
  return (
    <div>
      <div className="kr-overline mb-2">{title} ({items.length})</div>
      {items.length === 0 ? <div className="text-white/40 text-xs">None.</div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map((p) => (
            <div key={p[keyField]} className="border border-white/10 p-2.5">
              <div className="text-sm truncate">{p.name || p.slug || p[keyField]}</div>
              <div className="text-[10px] font-mono text-white/40 mt-0.5">{(p.created_at || "").slice(0, 10)} · {p[keyField]?.slice(0, 20)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreditsDialog({ userId, onClose }) {
  const [op, setOp] = useState("add");
  const [amount, setAmount] = useState(100);
  const [reason, setReason] = useState("");
  const submit = async () => {
    try {
      await api.patch(`/admin/users/${userId}/credits`, { op, amount: Number(amount), reason });
      toast.success("Credits updated");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-md" data-testid="admin-credits-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Coins className="h-5 w-5 text-[#FBBF24]" /> Adjust credits</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="flex gap-2">
            {["add", "remove", "set"].map((o) => (<button key={o} onClick={() => setOp(o)} className={`flex-1 h-9 border text-xs capitalize ${op === o ? "border-[#FBBF24] text-[#FBBF24]" : "border-white/10 text-white/60"}`} data-testid={`admin-credit-op-${o}`}>{o}</button>))}
          </div>
          <div><label className="kr-overline">{op === "set" ? "New balance" : "Amount"}</label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-mono" data-testid="admin-credit-amount" /></div>
          <div><label className="kr-overline">Reason</label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 bg-[#050505] border-white/10" placeholder="Audit trail" data-testid="admin-credit-reason" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-white/10">Cancel</Button><Button onClick={submit} className="bg-[#FBBF24] text-black hover:bg-[#FBBF24]/90" data-testid="admin-credit-submit">Apply</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ user, onClose }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email, role: user.role, plan: user.plan, reason: "" });
  const submit = async () => {
    try {
      await api.patch(`/admin/users/${user.user_id}`, form);
      toast.success("Updated");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-lg" data-testid="admin-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit user</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div><label className="kr-overline">Name</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-[#050505] border-white/10" /></div>
          <div><label className="kr-overline">Email</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-[#050505] border-white/10" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="kr-overline">Role</label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="mt-1 bg-[#050505] border-white/10"><SelectValue /></SelectTrigger><SelectContent className="bg-[#0A0A0E] border-white/10 text-white">{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="kr-overline">Plan</label><Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}><SelectTrigger className="mt-1 bg-[#050505] border-white/10"><SelectValue /></SelectTrigger><SelectContent className="bg-[#0A0A0E] border-white/10 text-white">{PLANS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><label className="kr-overline">Reason (audit trail)</label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1 bg-[#050505] border-white/10" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-white/10">Cancel</Button><Button onClick={submit} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90" data-testid="admin-edit-submit">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReasonDialog({ title, endpoint, onClose }) {
  const [reason, setReason] = useState("");
  const submit = async () => {
    try {
      await api.post(endpoint, { reason });
      toast.success(`${title} — done`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-md" data-testid="admin-reason-dialog">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (recorded in audit log)" className="w-full bg-[#050505] border border-white/10 text-sm p-3" data-testid="admin-reason-input" />
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-white/10">Cancel</Button><Button onClick={submit} className="bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90" data-testid="admin-reason-submit">Confirm</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ userId, onClose }) {
  const [pw, setPw] = useState("");
  const [mc, setMc] = useState(true);
  const [reason, setReason] = useState("");
  const submit = async () => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`, { new_password: pw || undefined, must_change: mc, reason });
      toast.success(`New password: ${data.temporary_password}`, { duration: 15000 });
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-md" data-testid="admin-reset-dialog">
        <DialogHeader><DialogTitle className="font-display">Reset password</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div><label className="kr-overline">New password (leave blank to auto-generate)</label><Input value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-mono" data-testid="admin-reset-password-input" /></div>
          <label className="flex items-center gap-2 text-xs text-white/70"><input type="checkbox" checked={mc} onChange={(e) => setMc(e.target.checked)} /> Force user to change at next login</label>
          <div><label className="kr-overline">Reason</label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 bg-[#050505] border-white/10" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-white/10">Cancel</Button><Button onClick={submit} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90" data-testid="admin-reset-submit">Reset</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ userId, email, onClose, onDeleted }) {
  const [mode, setMode] = useState("soft");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [reason, setReason] = useState("");
  const submit = async () => {
    if (mode === "permanent" && confirmEmail !== email) return toast.error("Type the email exactly to confirm permanent deletion.");
    try {
      await api.delete(`/admin/users/${userId}`, { params: { permanent: mode === "permanent", reason } });
      toast.success(mode === "permanent" ? "User permanently deleted" : "User soft-deleted");
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-lg" data-testid="admin-delete-dialog">
        <DialogHeader><DialogTitle className="font-display text-red-300 flex items-center gap-2"><Trash2 className="h-5 w-5" /> Delete user</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setMode("soft")} className={`p-3 border text-left ${mode === "soft" ? "border-[#F59E0B] text-[#F59E0B]" : "border-white/10 text-white/60"}`} data-testid="admin-delete-mode-soft">
              <div className="font-medium">Soft delete</div>
              <div className="text-[10px] text-white/40 mt-1">Disables login. Preserves records + audit. Restorable.</div>
            </button>
            <button onClick={() => setMode("permanent")} className={`p-3 border text-left ${mode === "permanent" ? "border-red-500 text-red-300" : "border-white/10 text-white/60"}`} data-testid="admin-delete-mode-permanent">
              <div className="font-medium">Permanent delete</div>
              <div className="text-[10px] text-white/40 mt-1">Purges user + projects + files + sessions. Audit + credit txns preserved. Irreversible.</div>
            </button>
          </div>
          {mode === "permanent" && (
            <div className="border border-red-500/30 bg-red-500/5 p-3">
              <div className="text-xs text-red-300 mb-2">Type <span className="font-mono">{email}</span> to confirm.</div>
              <Input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} className="bg-[#050505] border-white/10 font-mono" data-testid="admin-delete-confirm-email" />
            </div>
          )}
          <div><label className="kr-overline">Reason (recorded in audit)</label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 bg-[#050505] border-white/10" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-white/10">Cancel</Button><Button onClick={submit} className={`${mode === "permanent" ? "bg-red-500 hover:bg-red-500/90" : "bg-[#F59E0B] hover:bg-[#F59E0B]/90"} text-black`} data-testid="admin-delete-submit">Delete</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
